/**
 * "Handling an escalation" walkthrough.
 *
 * An informational tour — explains the escalation lifecycle, the CO review
 * workflow, and the Senior Manager approval branch. Mostly anchored to the
 * Escalations module's metrics bar and the preset filter tabs (visible to
 * management); falls back gracefully for staff users via showOn.
 *
 * Does NOT walk the user through clicking into a real escalation — that
 * would require a real Open escalation to exist, which can't be guaranteed.
 *
 * Status values, role-gating, and lifecycle wording mirror Escalations.vue
 * and EscalationReviewModal.vue — keep in sync if they evolve.
 *
 * Targets used:
 *   data-tour="escalations-metrics"   — top metrics row (mgmt only)
 *   data-tour="escalations-presets"   — preset filter tabs (mgmt only)
 *   data-tour="raise-escalation-btn"  — Raise Escalation button
 */

import { NEXT, BACK, START, SKIP, DONE, targetExists } from './_helpers.js';

// Inline-styled status pill, kept in sync with Escalations.vue table styling
// (definitions there are scoped, hence inlined here).
const STATUS_PILL = (label, bg, fg) =>
    `<span style="display:inline-block;font-size:11px;font-weight:700;padding:2px 10px;border-radius:8px;background:${bg};color:${fg};letter-spacing:0.3px;">${label}</span>`;

const open    = STATUS_PILL('Open',                 '#fee2e2', '#991b1b');
const review  = STATUS_PILL('Under Review',         '#fef3c7', '#92400e');
const pending = STATUS_PILL('Pending SM Approval',  '#dbeafe', '#1e40af');
const done    = STATUS_PILL('Resolved',             '#dcfce7', '#166534');

export const handleEscalationTour = {
    id: 'handle-escalation',
    title: 'Handling an escalation',
    description: 'The Compliance Officer / Senior Manager workflow for triaging, reviewing, and closing out escalations end-to-end.',
    autoLaunchOn: { page: 'escalations' },

    steps: [
        {
            id: 'intro',
            title: 'Handling an escalation',
            text: `Once a staff member raises an escalation, the Compliance Officer takes
                it through to closure — sometimes via the Senior Manager. This tour walks
                through that lifecycle.`,
            buttons: [START],
        },
        {
            id: 'lifecycle',
            title: 'The four statuses',
            text: `An escalation moves through up to four states:
                <div style="margin:10px 0;display:flex;flex-direction:column;gap:6px;">
                    <div>${open} &nbsp; newly raised, awaiting CO triage</div>
                    <div>${review} &nbsp; CO is investigating</div>
                    <div>${pending} &nbsp; CO is done, Senior Manager sign-off needed</div>
                    <div>${done} &nbsp; finalised, no further action</div>
                </div>
                Not every escalation needs SM approval — only those where the CO's compliance
                action is an SMR or Enhanced CDD on a high-risk client.`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'metrics',
            attachTo: { element: '[data-tour="escalations-metrics"]', on: 'bottom' },
            showOn: targetExists('[data-tour="escalations-metrics"]'),
            title: 'Top metrics (management view)',
            text: `At a glance: how many are <strong>Open</strong>,
                <strong>Under Review</strong>, <strong>Resolved in the last 30 days</strong>,
                <strong>SMRs in 30 days</strong>, and <strong>Overdue SMR Deadlines</strong>.
                Anything overdue is highlighted red.
                <br><br>
                A separate banner appears below if any escalations have been awaiting
                CO review for more than <strong>48 hours</strong> — those need urgent
                attention.`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'presets',
            attachTo: { element: '[data-tour="escalations-presets"]', on: 'bottom' },
            showOn: targetExists('[data-tour="escalations-presets"]'),
            title: 'Preset filters',
            text: `Quick tabs to narrow the list to common views — e.g. only Open escalations,
                only those Pending SM Approval, or only your own. Combine with the search box
                and detail filters below for finer queries.`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'co-review',
            title: 'CO review workflow',
            text: `Click any open escalation to open its detail page, then hit
                <strong>Begin Review</strong> (or <strong>Continue Review</strong>) to
                expand the inline review section. As CO you complete:
                <ul style="margin:6px 0 0 18px;padding:0;">
                    <li><strong>Assessment Notes</strong> (required) — what you found
                        on investigation.</li>
                    <li><strong>Compliance Action</strong> (required) — one of: <em>No
                        action required</em>, <em>Enhanced CDD required</em>, <em>SMR to be
                        submitted</em>, <em>Services suspended</em>, or <em>Other</em>.</li>
                    <li><strong>Service Instruction</strong> (required) — services
                        may continue, are suspended, or are on hold pending further review.</li>
                    <li><strong>Resolution Notes</strong> and <strong>Risk Flag</strong>
                        (optional) — additional context for the file.</li>
                </ul>`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'screening-matches',
            title: 'Sanctions / PEP match assessment',
            text: `If the escalation came from a positive sanctions, PEP, or other
                regulatory-list match, the detail page shows a <strong>Screening
                Matches — Assessment Required</strong> section above the review form.
                <br><br>
                You must mark each match as <em>Confirmed</em> or
                <em>False Positive</em> (with a brief justification) before the
                <strong>Submit</strong> button enables. The match cards include the
                source list, matched name, and the public data the platform pulls
                back so you can decide.`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'sm-branching',
            title: 'When SM approval kicks in',
            text: `Senior Manager sign-off is required when the CO's compliance action
                is <strong>SMR to be submitted</strong>, OR <strong>Enhanced CDD
                required / SMR to be submitted</strong> on a <strong>High-risk</strong>
                client — <em>and</em> the service instruction is
                <em>Services may continue</em>. If services are being suspended or put
                on hold, no SM approval is needed.
                <br><br>
                When SM is required the submit button reads <strong>Submit for SM
                Approval</strong> and the escalation moves to ${pending}. The SM then
                approves (escalation goes to ${done}) or sends it back for changes.
                Otherwise the button reads <strong>Submit Review</strong> and the
                escalation goes straight to ${done}.
                <br><br>
                <strong>Hard stop for sanctions matches:</strong> when the escalation
                type is a positive sanctions or regulatory-list match, the platform
                treats it as a statutory hard stop — there is no SM "approve" path,
                services must remain suspended, and the resolution flows through the
                AUSTRAC process.`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'detail-modal',
            title: 'What\'s on a closed escalation',
            text: `Once resolved, the detail modal carries the full history:
                <ul style="margin:6px 0 0 18px;padding:0;">
                    <li><strong>Escalation Details</strong> — reference, client, type,
                        dates, who raised it.</li>
                    <li><strong>CO Review</strong> — who reviewed, when, the compliance
                        action and service instruction taken, the assessment notes.</li>
                    <li><strong>Linked Records</strong> — SMR reference and Enhanced CDD
                        link if applicable.</li>
                    <li><strong>Audit Trail</strong> — every status change and action,
                        timestamped.</li>
                    <li><strong>Screening Matches</strong> — for sanctions/PEP-related
                        escalations, the match assessment table.</li>
                </ul>`,
            buttons: [BACK, DONE],
        },
    ],
};
